import {
  Alert,
  BreadcrumbGroup,
  Button,
  Container,
  ContentLayout,
  FormField,
  Header,
  Input,
  SpaceBetween,
  Table,
} from '@amzn/awsui-components-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { completeOrder, InProgressOrder, listInProgressOrders, prepareDish } from '../api/workshop';
import Layout from '../components/Layout';
import { UIPath } from '../paths';

export default function PrepareDish() {
  const navigate = useNavigate();
  const [participantId, setParticipantId] = useState('wtaliaf-kex');
  const [orderId, setOrderId] = useState(() => `order-${Date.now()}`);
  const [dishName, setDishName] = useState('Carbonara');
  const [quantity, setQuantity] = useState('1');
  const [message, setMessage] = useState<string>();
  const [accepted, setAccepted] = useState<boolean>();
  const [completed, setCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orders, setOrders] = useState<InProgressOrder[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [completingOrderId, setCompletingOrderId] = useState<string>();

  const refreshOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      setOrders(await listInProgressOrders());
    } catch {
      setAccepted(false);
      setMessage('PastaService could not load in-progress orders.');
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  useEffect(() => {
    refreshOrders();
  }, [refreshOrders]);

  const submit = async () => {
    setSubmitting(true);
    setCompleted(false);
    try {
      const response = await prepareDish({
        participantId,
        orderId,
        dishName,
        quantity: Number(quantity),
      });
      setAccepted(response.accepted);
      setMessage(`${response.message} Remaining servings: ${response.remainingServings}.`);
      if (response.accepted) {
        await refreshOrders();
      }
    } catch {
      setAccepted(false);
      setMessage('PastaService could not prepare the order.');
    } finally {
      setSubmitting(false);
    }
  };

  const complete = async (order: InProgressOrder) => {
    setCompletingOrderId(order.orderId);
    try {
      const response = await completeOrder(order.orderId);
      setAccepted(true);
      setCompleted(true);
      setMessage(`Order ${response.dish.orderId} is ${response.status} on the kitchen pass.`);
      await refreshOrders();
    } catch {
      setAccepted(false);
      setMessage(`PastaService could not complete order ${order.orderId}.`);
    } finally {
      setCompletingOrderId(undefined);
    }
  };

  return (
    <Layout
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: 'Home', href: UIPath.Landing },
            { text: 'Prepare Dish', href: UIPath.PrepareDish },
          ]}
          ariaLabel='Breadcrumbs'
        />
      }
      content={
        <ContentLayout header={<Header variant='h1'>Prepare a dish</Header>}>
          <SpaceBetween size='l'>
            {message !== undefined && (
              <Alert
                type={accepted ? 'success' : 'warning'}
                header={accepted ? (completed ? 'Dish ready' : 'Order in progress') : 'Order stopped'}
              >
                {message}
              </Alert>
            )}
            <Container>
              <SpaceBetween size='m'>
                <FormField label='Participant ID'>
                  <Input value={participantId} onChange={({ detail }) => setParticipantId(detail.value)} />
                </FormField>
                <FormField label='Order ID'>
                  <Input value={orderId} onChange={({ detail }) => setOrderId(detail.value)} />
                </FormField>
                <FormField label='Dish name'>
                  <Input value={dishName} onChange={({ detail }) => setDishName(detail.value)} />
                </FormField>
                <FormField label='Quantity'>
                  <Input type='number' value={quantity} onChange={({ detail }) => setQuantity(detail.value)} />
                </FormField>
                <SpaceBetween direction='horizontal' size='s'>
                  <Button variant='primary' loading={submitting} onClick={submit}>
                    Prepare dish
                  </Button>
                  <Button onClick={() => navigate(UIPath.ReadyDishes)}>View kitchen pass</Button>
                </SpaceBetween>
              </SpaceBetween>
            </Container>
            <Table
              header={
                <Header
                  variant='h2'
                  actions={
                    <Button loading={loadingOrders} onClick={refreshOrders}>
                      Refresh
                    </Button>
                  }
                >
                  Orders in progress
                </Header>
              }
              loading={loadingOrders}
              items={orders}
              empty='No orders are in progress.'
              columnDefinitions={[
                { id: 'dish', header: 'Dish', cell: (order) => order.dishName },
                { id: 'participant', header: 'Participant', cell: (order) => order.participantId },
                { id: 'order', header: 'Order', cell: (order) => order.orderId },
                { id: 'quantity', header: 'Quantity', cell: (order) => order.quantity },
                { id: 'status', header: 'Status', cell: (order) => order.status },
                {
                  id: 'actions',
                  header: 'Actions',
                  cell: (order) => (
                    <Button loading={completingOrderId === order.orderId} onClick={() => complete(order)}>
                      Complete
                    </Button>
                  ),
                },
              ]}
            />
          </SpaceBetween>
        </ContentLayout>
      }
    />
  );
}
